import type {
  BookExportArtifact,
  BookExportJob,
  BookExportReport,
  BookExportReportEntry,
  BookExportReportEntryKind,
  CanonicalBookExportManifest,
  RenderedBookExportTarget,
} from "./export-contract.ts";
import { collectManifestSectionKeys } from "./export-manifest.ts";
import {
  collectBookExportGeneratedSections,
  type BookExportCompositionSection,
} from "../../../supabase/functions/assemble-book/composition.ts";

export function createBookExportReportEntry(
  entry: BookExportReportEntry,
): BookExportReportEntry {
  return {
    ...entry,
    sourceId: entry.sourceId ?? null,
  };
}

export function buildBookExportReport(input: {
  compositionPlan?: BookExportCompositionSection[];
  entries?: BookExportReportEntry[];
  generatedAt?: string;
  manifest?: CanonicalBookExportManifest | null;
} = {}): BookExportReport {
  return {
    generatedAt: input.generatedAt ?? new Date().toISOString(),
    generatedSections: input.compositionPlan
      ? collectBookExportGeneratedSections(input.compositionPlan)
      : input.manifest
        ? collectManifestSectionKeys(input.manifest)
        : [],
    entries: (input.entries ?? []).map(createBookExportReportEntry),
  };
}

export function getBookExportEntriesByKind(
  report: BookExportReport | null | undefined,
  kind: BookExportReportEntryKind,
) {
  return (report?.entries ?? []).filter((entry) => entry.kind === kind);
}

export function hasBookExportReportEntries(report: BookExportReport | null | undefined) {
  return (report?.entries.length ?? 0) > 0;
}

export function canRetryBookExportJob(
  job: Pick<BookExportJob, "failureCode" | "status"> | null | undefined,
) {
  return job?.status === "failed" && job.failureCode !== "BOOK_EXPORT_STALE";
}

export function hasBookExportArtifacts(
  job: Pick<BookExportJob, "artifacts"> | null | undefined,
) {
  return (job?.artifacts.length ?? 0) > 0;
}

export function getLatestBookExportJob(jobs: BookExportJob[] | null | undefined) {
  return jobs?.[0] ?? null;
}

export function getBookExportArtifactForTarget(
  artifacts: BookExportArtifact[],
  target: RenderedBookExportTarget,
) {
  return artifacts.find((artifact) => artifact.target === target) ?? null;
}

export function getPrimaryBookExportArtifact(artifacts: BookExportArtifact[]) {
  return getBookExportArtifactForTarget(artifacts, "pdf")
    ?? getBookExportArtifactForTarget(artifacts, "docx")
    ?? null;
}
